<script setup lang="ts">
  import { ref, computed, onMounted, onBeforeUnmount, watchEffect } from "vue";
  import { useI18n } from "vue-i18n";
  import { Text, Button, IconScanAlarm, IconScanAlarmError, IconListCompleted, useNotification } from "@ui-kit";
  import { usePowerRfid, useReadRfid, useWriteRfid } from "@plugins";
  import { useProfileOptions } from "@src/hooks";
  import { StatusBar } from "@src/components";
  import { useGetWTFIR, WTFIRInputType, WTFIRTypeData, useLazyGetPairAlarm } from "@src/api";

  const emit = defineEmits<{ (e: "close"): void }>();

  const powerRfid = usePowerRfid();
  const readRfid = useReadRfid();
  const writeRfid = useWriteRfid();
  const { t } = useI18n();
  const { profile } = useProfileOptions();
  const { execute: getWTFIR } = useGetWTFIR();
  const { execute: getPairAlarm } = useLazyGetPairAlarm();
  const { show } = useNotification();

  const state = ref<"waiting" | "broken" | "success">("waiting");
  const timer = ref<{
    id: NodeJS.Timeout | null;
    value: number;
  }>({
    id: null,
    value: 3,
  });
  const title = computed(() => {
    if (state.value === "waiting") {
      return t("Приложите аларм{0}к RFID сканеру", ["\n"]);
    }

    if (state.value === "broken") {
      return t("Аларм сломан");
    }

    return t("Аларм в рабочем{0}состоянии", ["\n"]);
  });

  const description = computed(() => {
    if (state.value === "waiting") {
      return t("И дождитесь завершения проверки");
    }

    if (state.value === "broken") {
      return t("Положите его в корзину к нерабочим{0}алармам", ["\n"]);
    }

    return t("Можете использовать еще для{0}алармирования", ["\n"]);
  });

  const handleAddListener = async () => {
    if (state.value !== "waiting") {
      return;
    }

    if (profile.value?.constants.rfid.power_alarm_read) {
      await powerRfid.set(profile.value.constants.rfid.power_alarm_read);
    }

    readRfid.subscribe(async (data) => {
      if (!data.value) {
        show({ title: t("Аларм не найден"), type: "warning" });
      }

      const resultWTFIR = await getWTFIR({ data: data.value, input_type: WTFIRInputType.rfid });

      if (resultWTFIR.isError.value) {
        return;
      }

      if (resultWTFIR.data.value.type_data === WTFIRTypeData.epcAlarmClear) {
        await powerRfid.set(profile.value.constants.rfid.power_write);

        const resultWriteRfidData = await writeRfid.writeData({
          epc: data.value,
          filterData: data.value,
        });

        state.value = resultWriteRfidData ? "success" : "broken";
      } else if (resultWTFIR.data.value.type_data === WTFIRTypeData.epcAlarm) {
        const resultPairAlarm = await getPairAlarm({ epc: data.value });

        if (resultPairAlarm.isError.value) {
          return;
        }

        await powerRfid.set(profile.value.constants.rfid.power_write);

        const resultWriteRfidData = await writeRfid.writeData({
          epc: data.value,
          password: resultPairAlarm.data.value.key,
          filterData: data.value,
        });

        if (resultWriteRfidData) {
          state.value = "success";
        } else {
          const resultWriteRfidData = await writeRfid.writeData({ epc: data.value, filterData: data.value });

          state.value = resultWriteRfidData ? "success" : "broken";
        }
      } else {
        state.value = "broken";
      }

      await powerRfid.set(profile.value.constants.rfid.power_alarm_read || 1);
    });
  };

  const clearTimer = () => {
    clearTimeout(timer.value.id);
    timer.value.id = null;
    timer.value.value = 2;

    state.value = "waiting";
  };

  watchEffect(() => {
    if (state.value === "waiting") {
      clearTimer();
    }

    if (state.value === "broken" || state.value === "success") {
      if (timer.value.id) {
        clearTimer();
      }

      timer.value.id = setInterval(() => {
        timer.value.value -= 1;

        if (timer.value.value === 0) {
          clearTimer();
        }
      }, 1000);
    }
  });

  onMounted(handleAddListener);
  onBeforeUnmount(async () => {
    await powerRfid.set(profile.value.constants.rfid.power_alarm_read || 1);

    readRfid.unsubscribe();
    clearTimer();
  });
</script>

<template>
  <div class="widget__repair-alarm" data-testid="widget-repair-alarm">
    <StatusBar />
    <div class="repair-alarm__container">
      <div class="repair-alarm__content">
        <div class="repair-alarm__content__container">
          <IconScanAlarmError v-if="state === 'broken'" data-testid="repair-alarm-icon-error" />
          <IconListCompleted v-else-if="state === 'success'" data-testid="repair-alarm-icon-list-completed" />
          <IconScanAlarm v-else data-testid="repair-alarm-icon-scan" />
          <Text font="heading-H4" data-testid="repair-alarm-title">{{ title }}</Text>
          <Text color="secondary" data-testid="repair-alarm-description">{{ description }}</Text>
        </div>
      </div>
      <Button
        v-if="state === 'waiting'"
        variant="secondary"
        @click="emit('close')"
        data-testid="repair-alarm-button-cancel"
      >
        {{ t("Отмена") }}
      </Button>
      <template v-if="state === 'broken' || state === 'success'">
        <Button variant="secondary" @click="state = 'waiting'" data-testid="repair-alarm-button-check-again">
          {{ t(`Проверить ещё`) + ` (${timer.value})` }}
        </Button>
        <Button @click="emit('close')" data-testid="repair-alarm-button-main">{{ t("На главную") }}</Button>
      </template>
    </div>
  </div>
</template>

<style scoped>
  .widget__repair-alarm {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    background-color: var(--sf-base-background-white);
    z-index: 1;
  }

  .repair-alarm__container {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 0 16px 20px;
    gap: 8px;

    &:deep(.button) {
      width: 100%;
    }

    &:deep(.text) {
      white-space: break-spaces;
      text-align: center;
    }
  }

  .repair-alarm__content {
    display: flex;
    flex-direction: column;
    flex: 1;
    align-items: center;
    justify-content: center;
  }

  .repair-alarm__content__container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    &:deep(.text):first-of-type {
      margin-bottom: 8px;
      margin-top: 24px;
    }
  }
</style>
